import 'isomorphic-fetch';
import { ActionType, createAsyncAction, createAction } from 'typesafe-actions';
import { log404ErrorAsWarning } from '../../../common/apiAdapters/ErrorHandlers';
import { DataFetchingThunkAction } from '../types';
import { PageType } from '../../hooks/UrlManager/types';
import { convertListingPageStateToUrl } from '../../components/ListingPage/services/listingPageUrlService';
import { ListingPageUrlState } from '../products/types';
import {
    createDataFetchActions,
    FetchAction,
    SuccessFetchActionPayload,
    DefaultErrorPayload,
} from '../dataFetching/generators';
import { SeoResponse, SeoData } from './types';

const convertPageTypeToSeoUrl = (pageType: PageType): string => {
    switch (pageType) {
        case PageType.CATEGORY:
            return 'category';
        case PageType.BRANDS:
            return 'brand';
        case PageType.LANDING:
            return 'landing';
        case PageType.PDP:
            return 'product';
        default:
            return '';
    }
};

export const setDefaultSeoAction = createAction('@PLP_SEO/SET_DEFAULT_ACTION', action => {
    return (defaultData: SeoData, identifier: string) => action({ data: defaultData, identifier });
});

export const fetchSeoDataAction = createAsyncAction(
    '@PLP_SEO/FETCH_START',
    '@PLP_SEO/FETCH_SUCCESS',
    '@PLP_SEO/FETCH_FAILED'
)<FetchAction, SuccessFetchActionPayload<SeoResponse>, DefaultErrorPayload>();

export const fetchSeoDataIfNeeded = (
    urlState: ListingPageUrlState,
    defaultData: SeoData
): DataFetchingThunkAction => async (dispatch, getState, { apiService, logger }) => {
    const { listingPageType: type, listingPageKey } = urlState;
    const urlPath = `${convertPageTypeToSeoUrl(type)}/${listingPageKey}`;
    const identifier = convertListingPageStateToUrl(urlState);

    if (identifier === getState().seoData.identifier) {
        return;
    }

    if (
        ![PageType.BRANDS, PageType.CATEGORY, PageType.LANDING, PageType.PDP].includes(type) ||
        convertPageTypeToSeoUrl(type) === ''
    ) {
        dispatch(setDefaultSeoAction(defaultData, identifier));
        return;
    }

    dispatch(fetchSeoDataAction.request({ identifier }));
    try {
        const seoData = await apiService.shopApi.getSeoData(urlPath, urlState);
        dispatch(fetchSeoDataAction.success({ data: seoData, identifier }));
    } catch (err) {
        log404ErrorAsWarning(err, identifier, logger, 'fetchSeoDataIfNeeded');
        dispatch(fetchSeoDataAction.failure({ error: err, identifier }));
    }
};

export type SeoDataActionsType = ActionType<typeof fetchSeoDataAction> | ActionType<typeof setDefaultSeoAction>;

export const homePageSeoActions = createDataFetchActions(
    '@HOME_PAGE/SEO',
    ({ apiService }) => () => {
        return apiService.shopApi.getHomepageSeoData();
    },
    () => '@HOME_PAGE/SEO'
);

export type HomePageSeoActions = ActionType<typeof homePageSeoActions.actions>;
export const fetchHomePageSeoDataIfNeeded = homePageSeoActions.thunk;
